import React, {
  useState,
  useEffect,
  useCallback,
  MouseEvent,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton } from "@mui/material";
import { menuInfo, ChatSearchPanelHandle } from "@shared/type/CommonType";
import SnFormControls from "@shared/components/SnFormControls";
import {
  SnSortableFormArea,
  SnSortableFormControl,
  sortIdOf,
} from "@shared/components/SnSortableFormControls";
import { SnControlSelector } from "@shared/components/SnControlSelector"; // Assuming this is the correct import path
import { showPopupModal } from "@widgets/CommonModal";
import { global, onSearchStore } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import { showDialog } from "./CommonDialog";
import { buildSearchFormData } from "@shared/utils/CommonUtils";

interface SearchPanelProps {
  data: any;
  controls: any[];
  popupRef?: any;
  /**
   * 조건 grid 의 칸 폭 정책(FreeForm 과 같은 용도). 안 넘기면 기존 기본값을 쓴다.
   * 팝업은 기본 6칸이라 컨트롤이 많으면 줄이 넘치는데, 그 화면만 칸 수를 늘릴 때 쓴다.
   */
  gridTemplateColumns?: string;
  onValueChanged?: (event: ValueChangedEvent) => Record<string, any> | void;
  /**
   * 오른쪽 위 "조회조건 초기화" 버튼을 눌렀을 때. 초기값으로 되돌린 formData 를 넘겨주며,
   * 객체를 돌려주면 그 값으로 덮어쓴다. (화면별로 초기화 뒤 보정할 값이 있을 때만 쓴다)
   */
  onReset?: (nextData: any) => Record<string, any> | void;
  /** 초기화 버튼 숨김 (기본은 표시) */
  hideReset?: boolean;
  /**
   * 디자인 모드 (메뉴디자이너 미리보기용):
   * - 드래그&드랍 편집 모드(isDragOn)가 기본으로 켜진 상태로 시작 (이동/X삭제/리사이즈)
   *   단 onControlsChange 를 함께 넘기면(새 메뉴디자이너, 2026-10-02) 보통 미리보기로 시작하고 [배치 편집] 으로 들어가며,
   *   [항목 선택] 은 숨기고(그 디자이너의 X 는 초안에서 진짜 삭제) [편집 완료 · 편집 취소] 를 쓴다
   * - DB 저장(sys_formctrl) 메뉴는 숨김 — 미리보기 메타는 아직 DB에 없는 행이므로
   * - 우클릭 메뉴로 편집종료/설정 전환 가능 (SYSTEM 권한 불필요)
   */
  designMode?: boolean;
  /**
   * 편집 모드에서 칸을 옮기거나(이동) 지우거나(X) 크기를 바꾸거나(모서리) [추가]로 복원했을 때 부른다 (2026-10-02).
   * visible = 지금 보이는 칸(순서대로, qt_colspan · qt_rowspan 반영), hidden = 숨긴 칸.
   * 저장은 하지 않는다 — 받은 쪽이 정한다. 메뉴디자이너가 이 결과를 DB 가 아니라 초안(sys_design_formctrl)에 반영하려고 쓴다.
   * 안 넘기면 아무것도 부르지 않는다 — 기존 화면 동작 · 우클릭 메뉴 · [저장](개인 배치)은 그대로다.
   * 처음 그릴 때 · controls 가 바뀌어 다시 그릴 때는 부르지 않는다. 사람이 편집했을 때만 부른다.
   */
  onControlsChange?: (visible: any[], hidden: any[]) => void;
}

export interface ValueChangedEvent {
  newValue: any;
  oldValue: any;
  colId: string;
  rowData: any;
  source?: string;
}

type InputEls = {
  el: HTMLElement | null;
  isFocusable: boolean;
};

/** 우클릭 메뉴 제목 줄 — 작고 옅게 (2026-10-02) */
const menuHeadSx = { lineHeight: "28px", fontSize: 12, color: "#7d7e80", bgcolor: "transparent" };

const SearchPanel = forwardRef(
  (
    {
      data,
      controls = [], // 설정이 없는 화면도 있으므로 기본값 필수
      popupRef,
      gridTemplateColumns,
      onValueChanged,
      onReset,
      hideReset,
      designMode,
      onControlsChange,
    }: SearchPanelProps,
    ref
  ) => {
    const [formData, setFormData] = useState<any>();
    const [visibleControls, setVisibleControls] = useState<any[]>([]);
    const [hiddenControls, setHiddenControls] = useState<any[]>([]);
    const [editCon, setEditCon] = useState<any>();
    const [filter, setFilter] = useState<any>();
    const [isExpand, setIsExpand] = useState(false);
    const [dataVersion, setDataVersion] = useState(0);
    const [expandcss, setExpandcss] = useState(
      "overflow-hidden transition-all duration-500 ease-in-out max-h-[500px]"
    );

    const panelRef = useRef<HTMLDivElement>(null); // Panel 영역을 참조
    const [menuPosition, setMenuPosition] = useState<{
      mouseX: any;
      mouseY: any;
    } | null>(null);
    const inputEls = useRef<InputEls[]>([]);
    const [popupCons, setPopupCons] = useState<any>({}); /* */
    const formDataRef = useRef(formData);
    /* 새 디자이너(designMode + onControlsChange)는 보통 미리보기로 시작하고 우클릭 [배치 편집] 으로 들어간다 (2026-10-02).
       예전 AI 에디터(designMode 만, SN_SFA_ISSUE_REG_006)는 전처럼 편집 모드로 시작한다 — 기존 동작을 바꾸지 않는다 */
    /* 배치 편집에 들어가기 직전의 칸 목록 — [편집 취소] 때 되돌린다 (2026-10-02) */
    const layoutSnapshotRef = useRef<{ visible: any[]; hidden: any[] } | null>(null);
    const [isDragOn, setIsDragOn] = useState(!!designMode && !onControlsChange);
    /* 편집 결과를 받아 초안에 반영하는 새 디자이너인가 — 이때만 [항목 선택]을 숨기고 [편집 완료 · 편집 취소]를 쓴다 */
    const isDraftEditor = !!designMode && !!onControlsChange;
    /* 사람이 편집했다는 표시. 편집 함수가 올리고, 상태가 반영된 뒤 아래 effect 가 onControlsChange 를 부른다.
       편집 함수들은 함수형 갱신(prev => …)이라 그 안에서 바로 부르면 바뀐 값을 못 넘기고, 개발 모드에서 두 번 불린다 (2026-10-02) */
    const editSeqRef = useRef(0);
    const reportedSeqRef = useRef(0);
    /* 편집 취소용 — 편집 모드에서 처음 손대기 직전의 칸 목록. 편집 완료 · 취소 때 비운다 (2026-10-02) */
    const editSnapshotRef = useRef<{ visible: any[]; hidden: any[] } | null>(null);
    const markEdited = () => {
      if (!editSnapshotRef.current) {
        editSnapshotRef.current = { visible: visibleControls, hidden: hiddenControls };
      }
      editSeqRef.current += 1;
    };
    const [isSetFocus, setIsSetFocus] = useState(false);

    const { userInfo } = global();

    const onTextValueChanged = useCallback(
      async (param: string, data: string) => {
        const prevValue = formDataRef.current?.[param] ?? "";
        if (prevValue === data) return;

        const baseFormData = { ...formDataRef.current, [param]: data };
        formValueChanged(baseFormData);
        const event: ValueChangedEvent = {
          oldValue: prevValue,
          newValue: data,
          colId: param,
          rowData: baseFormData,
        };
        const updatedFields = await valueChangedEvent(event);
        const finalFormData = { ...baseFormData, ...updatedFields };

        formValueChanged(finalFormData);
      },
      [formData]
    );

    const onNumberValueChanged = useCallback(
      async (param: string, data: number) => {
        const prevValue = formDataRef.current?.[param] ?? 0;
        if (prevValue === data) return;

        const baseFormData = { ...formData, [param]: data };
        formValueChanged(baseFormData);
        const event: ValueChangedEvent = {
          oldValue: prevValue,
          newValue: data,
          colId: param,
          rowData: baseFormData,
        };
        const updatedFields = await valueChangedEvent(event);
        const finalFormData = { ...baseFormData, ...updatedFields };

        formValueChanged(finalFormData);
      },
      [formData]
    );

    const onPeriodValueChanged = useCallback(
      async (param: string, start: string, end: string, source?: string) => {
        const [startKey, endKey] = param.split(",").map((p) => p.trim());
        const prevStart = formDataRef.current?.[startKey] ?? "";
        const prevEnd = formDataRef.current?.[endKey] ?? "";

        if (prevStart === start && prevEnd === end) return;

        const baseFormData = {
          ...formDataRef.current,
          [startKey]: start,
          [endKey]: end,
        };
        formValueChanged(baseFormData);
        let finalFormData = baseFormData;
        if (prevStart !== start) {
          const event: ValueChangedEvent = {
            oldValue: prevStart,
            newValue: start,
            colId: startKey,
            rowData: baseFormData,
            source,
          };
          const updatedFields = await valueChangedEvent(event);
          finalFormData = { ...baseFormData, ...updatedFields };
        }
        if (prevEnd !== end) {
          const event: ValueChangedEvent = {
            oldValue: prevEnd,
            newValue: end,
            colId: endKey,
            rowData: baseFormData,
            source,
          };
          const updatedFields = await valueChangedEvent(event);
          finalFormData = { ...baseFormData, ...updatedFields };
        }

        formValueChanged(finalFormData);
      },
      [formData]
    );

    const onCodeValueChanged = useCallback(
      async (
        code: string,
        codeValue: string,
        name: string,
        nameValue: string,
        rowData?: string
      ) => {
        const prevValue = formDataRef.current?.[code] ?? "";
        if (prevValue === codeValue) return;

        let baseFormData = {
          ...formDataRef.current,
          [code]: codeValue,
          [name]: nameValue,
        };

        formValueChanged(baseFormData);

        const event: ValueChangedEvent = {
          oldValue: prevValue,
          newValue: codeValue,
          colId: code,
          rowData: baseFormData,
        };

        const updatedFields = await valueChangedEvent(event);
        const finalFormData = { ...baseFormData, ...updatedFields };

        formValueChanged(finalFormData);
      },
      [formData]
    );

    const formValueChanged = (data: any) => {
      formDataRef.current = data;
      setFormData(data);
      setChangeCon(data);
    };

    const getAllowedFieldNames = useCallback(() => {
      const fieldNames = new Set<string>();
      (visibleControls || []).forEach((control) => {
        String(control?.cd_param || "")
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
          .forEach((fieldName) => fieldNames.add(fieldName));
        const displayFieldName = String(control?.cd_displaycolumn || "").trim();
        if (displayFieldName) {
          fieldNames.add(displayFieldName);
        }
      });
      return Array.from(fieldNames);
    }, [visibleControls]);

    /* 챗봇이 넣은 이름을 찾아 채울 칸 — 코드 칸(도움창 있음)과 콤보 칸 (2026-10-02).
       여러 개 고르는 코드 칸(SnMultiCodeBox)도 — 빠져 있어 "매일유업" 이 코드로 안 바뀌고 그대로 조회됐다 (2026-10-14) */
    const getChatFields = useCallback(
      () =>
        (visibleControls || [])
          .filter(
            (control: any) =>
              ((control?.tp_ctrl === "SnCodeBox" || control?.tp_ctrl === "SnMultiCodeBox") && control?.cd_popup) ||
              control?.tp_ctrl === "SnComboBox"
          )
          .map((control: any) => ({
            param: String(control.cd_param || "").split(",")[0].trim(),
            tp_ctrl: control.tp_ctrl,
            nm_label: control.nm_label,
            cd_popup: control.cd_popup,
            cd_clas: control.cd_clas,
            cd_displaycolumn: control.cd_displaycolumn,
            js_combo: control.js_combo,
          }))
          .filter((field: any) => field.param),
      [visibleControls]
    );

    useEffect(() => {
      /* 자기 설정(메뉴 코드)을 받은 뒤 한 번만 등록한다. 설정 전에 "지금 보는 메뉴" 이름으로 임시 등록했다 지우면서
         그 메뉴(발주등록) 것까지 지워 챗봇이 조회조건을 못 찾았다 (2026-10-16) */
      const menuCode = (controls || []).find((control) => control?.cd_menu)?.cd_menu;
      if (!menuCode) return;

      const handle: ChatSearchPanelHandle = {
        getFormdata: () => formDataRef.current,
        setFormdata: (nextData) => formValueChanged(nextData),
        getAllowedFieldNames,
        getChatFields,
        /* 챗봇 "조회조건 초기화" — ↻ 버튼과 같은 처리. 버튼을 숨긴 화면은 내주지 않는다 (2026-10-16) */
        ...(hideReset ? {} : { resetFormdata: () => resetRef.current() }),
      };
      onSearchStore.getState().registerSearchPanel(menuCode, handle);
      return () => {
        /* 내가 맡긴 것일 때만 지운다 — 한 화면에 조회패널이 둘이면 서로 지우지 않게 */
        onSearchStore.getState().unregisterSearchPanel(menuCode, handle);
      };
    }, [controls, getAllowedFieldNames, getChatFields, hideReset]);

    const valueChangedEvent = async (event: ValueChangedEvent) => {
      if (onValueChanged) {
        return (await onValueChanged?.(event)) || {};
      }
    };

    const fetchControl = async () => {
      try {
        const visibleFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() == "Y"
        );
        setVisibleControls(visibleFilters);
        const hiddenFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() != "Y"
        );
        setHiddenControls(hiddenFilters);
      } catch (error: any) {}
    };

    const fetchData = async () => {
      try {
        requestAnimationFrame(() => {
          setChangeCon(data);
          setFormData(data);
          formDataRef.current = data;
          setDataVersion((prev) => prev + 1);
          if (data != null && !isSetFocus) {
            setIsSetFocus(true);
          }
        });
      } catch (error: any) {}
    };

    const setChangeCon = useCallback(
      (change: any) => {
        const editObj: Record<string, any> = {};
        const filterObj: Record<string, any> = {};
        const visibleObj: Record<string, boolean> = {};
        const editCons = controls.filter(
          (control) =>
            control.dc_editcon &&
            control.dc_editcon.length > 0 &&
            control.dc_editcon.trim() != ""
        );
        // editCon 처리
        if (editCons && change) {
          editCons.forEach((con) => {
            let conData = con.dc_editcon;

            if (change.state === "insert") {
              conData = conData.replace("new", "false");
            } else {
              conData = conData.replace("new", "true");
            }

            Object.keys(change).forEach((key) => {
              conData = conData.replaceAll(key, change[key]);
            });

            editObj[con.cd_param] = conData;
          });
          setEditCon(editObj);
        }
        const filters = controls.filter(
          (control) =>
            control.dc_filter &&
            control.dc_filter.length > 0 &&
            control.dc_filter.trim() != ""
        );
        // filter 처리
        if (filters && change) {
          filters.forEach((fil) => {
            let filData = fil.dc_filter;
            Object.keys(change).forEach((key) => {
              filData = filData.replaceAll(`{${key}}`, change[key]);
            });
            filterObj[fil.cd_param] = filData;
          });
          setFilter(filterObj);
        }

        // visible 처리
        if (visibleControls) {
          visibleControls.forEach((control) => {
            const raw = editObj?.[control.cd_param];
            if (raw && change.hasOwnProperty(control.cd_param)) {
              try {
                visibleObj[control.cd_param] = Boolean(
                  new Function(`return ${raw};`)()
                );
              } catch (e) {
                visibleObj[control.cd_param] = false;
                console.error("eval error in setChangeCon:", raw, e);
              }
            } else {
              visibleObj[control.cd_param] = false;
            }
          });
        }

        // 필요시 visibleObj 저장 또는 상태 처리 가능
      },
      [visibleControls, controls]
    );

    useEffect(() => {
      fetchData();
    }, [data]);

    useEffect(() => {
      if (isSetFocus)
        requestAnimationFrame(() => {
          if (popupRef) {
            focusLastInput();
          } else {
            focusInputByIndex(0);
          }
        });
    }, [isSetFocus]);

    useEffect(() => {
      fetchControl();
    }, [controls]);

    const focusInputByIndex = async (index: number) => {
      const input = inputEls.current[index]?.el;
      if (input) {
        if (input.tabIndex != -1) {
          input.focus();
        }
      }
    };

    const isFocusableItem = (item?: InputEls) =>
      !!item?.el && item.isFocusable && item.el.tabIndex !== -1;

    const focusLastInput = () => {
      const list = inputEls.current;

      // cd_param이 'str_search' 이거나 nm_label이 '검색'인 컨트롤이 있으면 우선 포커스
      // 단, 라벨이 '검색'이어도 콤보(검색구분 fg_search 등)는 포커스 대상에서 제외
      const targetIndex = (visibleControls || []).findIndex((control) => {
        const params = String(control?.cd_param || "")
          .split(",")
          .map((p) => p.trim());
        const label = String(control?.nm_label || "").trim();
        const tpCtrl = String(control?.tp_ctrl || "")
          .trim()
          .replace("AfterLabel", "");
        const isCombo = tpCtrl.startsWith("SnComboBox");
        return params.includes("str_search") || (label === "검색" && !isCombo);
      });

      if (targetIndex >= 0 && isFocusableItem(list[targetIndex])) {
        list[targetIndex]!.el!.focus();
        return;
      }

      // 없으면 마지막 focusable 컨트롤에 포커스
      for (let i = list.length - 1; i >= 0; i--) {
        if (isFocusableItem(list[i])) {
          list[i].el!.focus();
          break;
        }
      }
    };

    const gridContainerStyle = {
      display: "grid",
      gridTemplateColumns:
        gridTemplateColumns ||
        (popupRef
          ? "repeat(6, minmax(125px, 1fr))"
          : // 85px 은 작은 화면에서 날짜 컨트롤 등이 가려지지 않는 최소폭.
            "repeat(12, minmax(85px, 1fr))"),
      gridTemplateRows: "repeat(auto-fill, 1fr)",
      overflowX: "auto" as "auto",
      gap: "10px",
      padding: "11px",
    };

    /* 편집 결과 알림 — 사람이 편집했을 때만(editSeq 가 올랐을 때만) 부른다 */
    useEffect(() => {
      if (!onControlsChange) return;
      if (editSeqRef.current === reportedSeqRef.current) return;
      reportedSeqRef.current = editSeqRef.current;
      onControlsChange(visibleControls, hiddenControls);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleControls, hiddenControls]);

    const moveControl = (fromIdx: number, toIdx: number) => {
      markEdited();
      setVisibleControls((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(fromIdx, 1); // 1. 기존 위치에서 제거
        updated.splice(toIdx, 0, moved); // 2. 목표 인덱스에 삽입
        return updated;
      });
    };

    const removeControl = (index: number, id: number) => {
      markEdited();
      setVisibleControls((prev) => {
        const updated = [...prev];
        updated.splice(index, 1); // 1. 기존 위치에서 제거
        return updated;
      });

      // controls에서 no_line 값이 Id와 같은 control을 찾아 hiddenControls에 추가
      const controlToHide = controls.find((control) => control.no_line === id);
      if (controlToHide) {
        setHiddenControls((prev) => [...prev, controlToHide]);
      }
    };

    /* 크기 조정 — 부품이 그리드의 실제 칸 폭으로 계산해 1칸 ~ 한 줄 전체로 맞춘 값을 손을 놓을 때 한 번 넘긴다.
       전에는 끄는 동안 계속 불리며 85px 고정으로 계산해 실제 칸보다 빨리 커지고, 0칸 · 넘치는 칸이 생겼다 (2026-10-06) */
    const resizeControl = (id: number, colspan: number, rowspan: number) => {
      markEdited();
      setVisibleControls((prev) =>
        prev.map((c) =>
          c.no_line === id
            ? { ...c, qt_colspan: colspan, qt_rowspan: rowspan }
            : c
        )
      );
    };

    const handleRightClick = (event: MouseEvent) => {
      event.preventDefault(); // 기본 메뉴 비활성화

      // 자식 요소 위에서 우클릭을 했을 때는 아무 동작도 하지 않음
      if (panelRef.current) {
        const panelArea = panelRef.current.getBoundingClientRect(); // panel 영역
        const { clientX: x, clientY: y } = event; // 클릭 좌표

        // 클릭 좌표가 panel 영역 내에 있는지 확인
        const isInsidePanel =
          x >= panelArea.left &&
          x <= panelArea.right &&
          y >= panelArea.top &&
          y <= panelArea.bottom;

        if (isInsidePanel) {
          // 모든 자식 요소의 위치를 가져와서 클릭된 위치와 비교
          const children = Array.from(panelRef.current.children);
          const isOnChild = children.some((child) => {
            const childRect = (child as HTMLElement).getBoundingClientRect();
            return (
              x >= childRect.left &&
              x <= childRect.right &&
              y >= childRect.top &&
              y <= childRect.bottom
            );
          });

          if (!isOnChild && canOpenMenu) {
            // 자식 요소 위가 아닌 빈 공간 클릭 -> 메뉴 열기. 보여 줄 항목이 없으면(일반 사용자 평소) 띄우지 않는다 (2026-10-02)
            setMenuPosition({ mouseX: x, mouseY: y });
          }
        } else {
          handleMenuClose(); // panel 외부에서 클릭하면 메뉴 닫기
        }
      }
    };

    const handleMenuClose = () => {
      setMenuPosition(null);
    };

    /* 배치 편집은 SYSTEM 계정 · 디자이너에서만. 편집 중이면 늘 메뉴가 있다 (2026-10-02 — 쓰지 않던 Option 1 · 2 · 3 삭제) */
    const canEditLayout = !!designMode || global.getState().userInfo.cd_user === "SYSTEM";
    const canOpenMenu = isDragOn || canEditLayout;

    const setFormControl = () => {
      setIsDragOn((prev) => !prev); // 상태로 토글 → 리렌더 보장
      handleMenuClose();
    };

    const addFormControl = () => {
      /* 숨긴 칸은 fg_visible 이 그대로 'Y' 라 팝업에서 체크된 채로 보였다 — 열 때 보이는 칸 Y · 숨긴 칸 N 으로 맞춘다 (2026-10-02) */
      const Controls = [
        ...visibleControls.map((c) => ({ ...c, fg_visible: "Y" })),
        ...hiddenControls.map((c) => ({ ...c, fg_visible: "N" })),
      ];
      showPopupModal({
        title: "항목 선택",
        okayBtn: false,
        closeBtn: false,
        width: "250px",
        height: "400px",
        children: (
          <SnControlSelector
            initialControls={Controls}
            onApply={(visibleControls, hiddenControls) => {
              markEdited();
              setVisibleControls(visibleControls || []);
              setHiddenControls(hiddenControls || []);
            }}
          />
        ),
      });

      handleMenuClose();
    };

    const saveFormControl = async () => {
      const hasCdModule = controls.some(
        (control) =>
          control.cd_module !== undefined && control.cd_module !== null
      );
      const isNew = controls.some(
        (control) => control.cd_user !== userInfo?.cd_user
      );

      const mapControl = (control: any, idx: number, visible: boolean) => ({
        cd_firm: control.cd_firm,
        cd_menu: control.cd_menu,
        cd_form: control.cd_form,
        no_line: control.no_line,
        cd_user: userInfo?.cd_user,
        qt_rowspan: control.qt_rowspan,
        qt_colspan: control.qt_colspan,
        fg_visible: visible ? "Y" : "N",
        no_seq: visible ? idx + 1 : visibleControls.length + idx + 1,
        ...(isNew && {
          tp_ctrl: control.tp_ctrl,
          nm_label: control.nm_label,
          cd_value: control.cd_value,
          tp_format: control.tp_format,
          cd_param: control.cd_param,
          cd_clas: control.cd_clas,
          dc_sql: control.dc_sql,
          cd_popup: control.cd_popup,
          yn_required: control.yn_required,
          dc_editcon: control.dc_editcon,
          cd_displaycolumn: control.cd_displaycolumn,
          dc_filter: control.dc_filter,
        }),
        ...(hasCdModule && { cd_module: control.cd_module }),
      });

      const vControls = visibleControls.map((control, idx) =>
        mapControl(control, idx, true)
      );
      const hControls = hiddenControls.map((control, idx) =>
        mapControl(control, idx, false)
      );

      const updatedControls = [...vControls, ...hControls];
      const save = isNew
        ? { insert: updatedControls }
        : { update: updatedControls };

      const SaveData = !hasCdModule
        ? { sys_formctrl: save }
        : { sys_popupctrl: save };

      handleMenuClose();
      /* 저장되면 보통 모드로 돌아간다. 전에는 저장 뒤에도 편집 모드로 남아 [취소]를 한 번 더 눌러야 했다 (2026-10-02) */
      const saved = await fnSave(
        !hasCdModule
          ? "SN_SYS_BAS_REG_006_SAVE_01"
          : "SN_SYS_BAS_REG_008_SAVE_01",
        SaveData
      );
      if (saved !== false) {
        layoutSnapshotRef.current = null;
        setIsDragOn(false);
      }
    };

    const handleMenuRightClick = (event: React.MouseEvent) => {
      event.preventDefault();
      handleMenuClose();
      handleRightClick(event);
    };

    /**
     * 조회조건 초기화: 컨트롤 설정(cd_value)으로 만든 초기값으로 되돌린다.
     * 컨트롤에 없는 키(화면이 따로 넣어준 값)는 유지하고, 컨트롤 항목만 초기값으로 덮어쓴다.
     * 컨트롤이 내부 state 를 가진 경우가 있어 dataVersion 을 올려 다시 그린다.
     */
    const resetFormdata = () => {
      let nextData: any = {
        ...(formDataRef.current || {}),
        ...buildSearchFormData(controls),
      };
      const patched = onReset?.(nextData);
      if (patched && typeof patched === "object") {
        nextData = { ...nextData, ...patched };
      }
      formValueChanged(nextData);
      setDataVersion((prev) => prev + 1);
    };
    /* 챗봇이 부를 때도 그때의 화면 처리(onReset)로 — 등록은 controls 가 바뀔 때만 다시 한다 */
    const resetRef = useRef(resetFormdata);
    resetRef.current = resetFormdata;

    const handleExpand = (flag?: any) => {
      const f = typeof flag === "boolean" ? !flag : !isExpand;
      if (f) {
        setExpandcss(
          "overflow-hidden transition-all duration-500 ease-in-out max-h-[42px]"
        );
      } else {
        setExpandcss(
          "overflow-hidden transition-all duration-500 ease-in-out max-h-[500px]"
        );
      }
      setIsExpand(f);
    };

    useImperativeHandle(ref, () => ({
      getFormdata: () => {
        return formDataRef.current;
      },
      setFormdata: (data: any) => formValueChanged(data),
      resetFormdata,
      getAllowedFieldNames,
      applyPopupConditions: (popupConditions: any) =>
        setPopupCons(popupConditions),
      handleExpand: (flag: any) => {
        handleExpand(flag);
      },
      requiredValuesCheck,
      focusLastInput,
    }));

    const setInputRef = (
      el: HTMLElement | null,
      index: number,
      isReadOnly: boolean
    ) => {
      inputEls.current[index] = {
        el,
        isFocusable: !isReadOnly,
      };
    };

    const handleMoveFocus = (fromIndex: number, target: string) => {
      if (target === "current") {
        const el = inputEls.current?.[fromIndex].el;
        if (el) {
          el.focus();
        }
      } else if (target === "next") {
        const list = inputEls.current;
        for (let i = fromIndex + 1; i < list.length; i++) {
          const entry = list[i];
          if (entry?.el && entry.isFocusable) {
            entry.el.focus();
            return;
          }
        }

        // 끝까지 없으면 현재 blur
        list[fromIndex]?.el?.blur?.();
      }
    };

    const setReadOnly = useCallback(
      (param: string) => {
        if (!editCon || !Object.prototype.hasOwnProperty.call(editCon, param)) {
          return false;
        }

        const condition = editCon[param];

        // 문자열 조건을 안전하게 평가 (true/false로 예상되는 JS 식)
        try {
          return Boolean(Function(`"use strict"; return (${condition})`)());
        } catch (e) {
          console.warn(
            `Invalid expression for editCon[${param}]:`,
            condition,
            e
          );
          return false;
        }
      },
      [editCon]
    );

    const requiredValuesCheck = () => {
      try {
        const requireds = controls.filter((col) => col.yn_required === "Y");

        requireds.forEach((required) => {
          const key = required.cd_param; // 또는 nm_column, 실제 데이터 키에 맞게 조정
          const value = formDataRef.current[key];

          if (required.tp_ctrl === "SnNumberBox") {
            if (value == null || value === "0") {
              showDialog("alter", `${required.nm_label}은(는) 필수조건입니다.`);
              throw "finish";
            }
          } else {
            if (value == null || value === "") {
              showDialog("alter", `${required.nm_label}은(는) 필수조건입니다.`);
              throw "finish";
            }
          }
        });
        return true;
      } catch (Exception) {
        if (Exception !== "finish") throw Exception;
        else return false;
      }
    };

    // 전역 CSS(src/styles/index.css) 의 .relative 가 flex 로 덮어쓰므로 tailwind 클래스 대신 인라인 style
    return (
      <div style={{ position: "relative" }}>
        {/* 초기화 버튼을 absolute 로 띄우면 조건이 넘쳐 가로 스크롤될 때 컨트롤 위에 겹친다.
            grid 옆 flex 칸으로 두고, grid 는 minWidth 0 으로 자기 칸 안에서만 스크롤한다. */}
        <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div className={expandcss} style={{ flex: 1, minWidth: 0 }}>
          <div
            style={gridContainerStyle}
            onContextMenu={handleRightClick}
            ref={panelRef}
          >
            {/* panelRef가 잡힌 뒤에만 백엔드 루트를 그쪽으로 제한 */}
            {/* 배치 편집 — 끄는 동안은 이웃 칸만 비켜 나고, 순서 · 크기는 손을 놓을 때 한 번 바뀐다 (2026-10-06, react-dnd → dnd-kit) */}
            {isDragOn ? (
              <SnSortableFormArea
                ids={(visibleControls || []).map(sortIdOf)}
                onMove={moveControl}
              >
                <>
                  {visibleControls != null &&
                    visibleControls.length > 0 &&
                    visibleControls.map((control, index) => (
                      <SnSortableFormControl
                        key={`${control.no_line}-${control.cd_param}-${dataVersion}`}
                        sortId={sortIdOf(control)}
                        labletype="SearchPanel"
                        control={control}
                        id={control.no_line}
                        index={index}
                        formData={formData}
                        requireds={{}}
                        popupCons={popupCons}
                        setRef={() => {}}
                        setInputRef={setInputRef}
                        handleMoveFocus={handleMoveFocus}
                        onTextValueChanged={onTextValueChanged}
                        onNumberValueChanged={onNumberValueChanged}
                        onPeriodValueChanged={onPeriodValueChanged}
                        onCodeValueChanged={onCodeValueChanged}
                        setReadOnly={setReadOnly}
                        onButtonClick={() => {}}
                        isSearchPanel={true}
                        popupRef={popupRef}
                        removeControl={removeControl}
                        resizeControl={resizeControl}
                        filter={filter}
                        removeAtLabel={!!designMode}
                      />
                    ))}
                </>
              </SnSortableFormArea>
            ) : (
              <>
                {visibleControls != null &&
                  visibleControls.length > 0 &&
                  visibleControls.map((control, index) => (
                    <SnFormControls
                      key={`${control.no_line}-${control.cd_param}-${dataVersion}`}
                      labletype="SearchPanel"
                      control={control}
                      index={index}
                      formData={formData}
                      requireds={{}}
                      popupCons={popupCons}
                      setRef={() => {}}
                      setInputRef={setInputRef}
                      handleMoveFocus={handleMoveFocus}
                      onTextValueChanged={onTextValueChanged}
                      onNumberValueChanged={onNumberValueChanged}
                      onPeriodValueChanged={onPeriodValueChanged}
                      onCodeValueChanged={onCodeValueChanged}
                      setReadOnly={setReadOnly}
                      onButtonClick={() => {}}
                      isSearchPanel={true}
                      popupRef={popupRef}
                      filter={filter}
                    />
                  ))}
              </>
            )}
            <Menu
              open={menuPosition !== null}
              onClose={handleMenuClose}
              onContextMenu={handleMenuRightClick}
              anchorReference="anchorPosition"
              anchorPosition={
                menuPosition === null
                  ? undefined
                  : { top: menuPosition.mouseY, left: menuPosition.mouseX }
              }
            >
              {/* 우클릭 메뉴 (2026-10-02) — 색 아이콘(배치 편집 · 저장 파랑, 항목 선택 하늘, 완료 초록, 취소 빨강) · 제목. 평소 [배치 편집], 편집 중 일반 화면 [항목 선택 · 저장 · 편집 취소],
                  편집 중 디자이너 [편집 완료 · 편집 취소]. 동작은 전과 같고 이름 · 모양만 바꿨다 */}
              {!isDragOn && canEditLayout && [
                <ListSubheader key="h" sx={menuHeadSx}>조회조건</ListSubheader>,
                <MenuItem key="edit" dense onClick={() => {
                    layoutSnapshotRef.current = { visible: visibleControls, hidden: hiddenControls };
                    setFormControl();
                  }}>
                  <ListItemIcon><TuneIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText>배치 편집</ListItemText>
                </MenuItem>,
              ]}
              {isDragOn && !designMode && [
                <ListSubheader key="h" sx={menuHeadSx}>조회조건 편집</ListSubheader>,
                <MenuItem key="pick" dense onClick={() => addFormControl()}>
                  <ListItemIcon><PlaylistAddCheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText>항목 선택</ListItemText>
                </MenuItem>,
                <MenuItem key="save" dense onClick={() => saveFormControl()}>
                  <ListItemIcon><SaveIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText>저장</ListItemText>
                </MenuItem>,
                <Divider key="d" />,
                <MenuItem key="cancel" dense onClick={() => {
                    /* 편집 취소 — 배치 편집 전으로 되돌리고 보통 모드로 (2026-10-02) */
                    const snap = layoutSnapshotRef.current;
                    layoutSnapshotRef.current = null;
                    if (snap) {
                      setVisibleControls(snap.visible);
                      setHiddenControls(snap.hidden);
                    }
                    setFormControl();
                  }}>
                  <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>편집 취소</ListItemText>
                </MenuItem>,
              ]}
              {/* 예전 AI 에디터(designMode 만) — 전과 같은 [추가](항목 선택) · [편집종료](편집 완료) */}
              {isDragOn && designMode && !isDraftEditor && [
                <ListSubheader key="h" sx={menuHeadSx}>조회조건 편집</ListSubheader>,
                <MenuItem key="pick" dense onClick={() => addFormControl()}>
                  <ListItemIcon><PlaylistAddCheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText>항목 선택</ListItemText>
                </MenuItem>,
                <MenuItem key="done" dense onClick={() => setFormControl()}>
                  <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText>편집 완료</ListItemText>
                </MenuItem>,
              ]}
              {/* 새 디자이너 — 편집 완료는 바꾼 것을 둔 채 끝, 편집 취소는 이번 편집을 되돌리고 끝. 저장은 화면 툴바 */}
              {isDragOn && isDraftEditor && [
                <ListSubheader key="h" sx={menuHeadSx}>조회조건 편집</ListSubheader>,
                <MenuItem
                  key="done"
                  dense
                  onClick={() => {
                    editSnapshotRef.current = null;
                    setFormControl();
                  }}
                >
                  <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText>편집 완료</ListItemText>
                </MenuItem>,
                <Divider key="d" />,
                <MenuItem
                  key="cancel"
                  dense
                  onClick={() => {
                    const snap = editSnapshotRef.current;
                    editSnapshotRef.current = null;
                    if (snap) {
                      editSeqRef.current += 1; /* 되돌린 것도 알린다 — 받은 쪽 초안이 함께 돌아간다 */
                      setVisibleControls(snap.visible);
                      setHiddenControls(snap.hidden);
                    }
                    setFormControl();
                  }}
                >
                  <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>편집 취소</ListItemText>
                </MenuItem>,
              ]}
            </Menu>
          </div>
        </div>
        {!hideReset && !isDragOn && (
          <IconButton
            size="small"
            title="조회조건 초기화"
            aria-label="조회조건 초기화"
            tabIndex={-1}
            onClick={resetFormdata}
            sx={{
              flexShrink: 0,
              marginTop: "12px",
              padding: "2px",
              color: "gray",
              "&:hover": { color: "blue" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        </div>
        <div className="flex justify-center h-[15px]">
          <IconButton
            onClick={handleExpand}
            sx={{
              color: "gray", // 기본 색상
              "&:hover": {
                color: "blue", // hover 시 색상
                transform: "scale(1.2)", // hover 시 크기 확대
                transition: "all 0.3s ease", // 부드러운 애니메이션
              },
            }}
          >
            {!isExpand && <ArrowDropUpIcon />}
            {isExpand && <ArrowDropDownIcon />}
          </IconButton>
        </div>
        <hr
          style={{ height: "1px", background: "rgb(186,191,199)", border: 0 }}
        />
      </div>
    );
  }
);

export default React.memo(SearchPanel);
